import { canApplyDeviceUpdate } from "./deviceProgress";
// Bridges service-worker update detection (src/index.tsx, which runs before
// React mounts) to a React component that can show the user a choice
// instead of silently reloading out from under them mid-session. See
// UpdateAvailableBanner - only one listener (that banner) is expected in
// practice, but this supports more without assuming so.
type Listener = (registration: ServiceWorkerRegistration) => void;

let listeners: Listener[] = [];
let pendingRegistration: ServiceWorkerRegistration | null = null;

export function notifyUpdateAvailable(registration: ServiceWorkerRegistration): void {
    pendingRegistration = registration;
    listeners.forEach((listener) => listener(registration));
}

// Returns an unsubscribe function. If an update was already signaled before
// this subscriber mounted (a real possibility - the service worker check
// runs immediately on load, independent of React's own mount timing), the
// listener is called immediately with the pending registration so a banner
// mounted after the fact still shows up.
export function subscribeToUpdateAvailable(listener: Listener): () => void {
    listeners.push(listener);
    if (pendingRegistration) listener(pendingRegistration);
    return () => {
        listeners = listeners.filter((existing) => existing !== listener);
    };
}

// Activates the waiting service worker and reloads once it takes over -
// the same mechanism index.tsx already uses for the "nothing painted yet"
// case, just deferred until the user actually asks for it here.
export async function applyUpdate(registration: ServiceWorkerRegistration): Promise<void> {
    if (!await canApplyDeviceUpdate()) throw new Error("Pending device progress");
    if (!registration.waiting) return;
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
}

// Test-only - this module's state is a shared closure that would otherwise
// leak between test cases in the same file (e.g. a pending registration
// from one test replaying into the next test's fresh subscriber).
export function __resetForTests(): void {
    listeners = [];
    pendingRegistration = null;
}
